import React, { useState, useEffect } from 'react'
import {
    Button,
    CircularProgress,
    TableSortLabel,
    Theme,
    IconButton,
    Tooltip,
    makeStyles,
    FormControl,
    MenuItem,
    Select,
    useTheme,
} from '@material-ui/core'
import Pagination from '@material-ui/lab/Pagination'
import AddIcon from '@material-ui/icons/Add'
import { Container } from '../../../components'
import { ServiceOrderAnalytic } from '../../../hooks/useEZFolioWorkorderAnalytics'
import { WorkspaceProjectsRow } from './WorkspaceProjectsRow'
import {
    useWorkspaceProjectAnalytics,
    WorkspaceProjectsAnalytics,
} from '../../../hooks/useEZFolioProject'

import {
    ApartmentNameASC,
    ApartmentNameDESC,
    TotalCreatedASC,
    TotalCreatedDESC,
    ActiveASC,
    ActiveDESC,
    BehindASC,
    BehindDESC,
    FutureASC,
    FutureDESC,
    ActiveProjectsASC,
    ActiveProjectsDESC,
    FutureProjectsASC,
    FutureProjectsDESC,
    RecentASC,
    RecentDESC,
} from './ProjectTableCell'
import { useUserTags } from '../../../contexts/UserTagsContext'
import { Chip } from '@material-ui/core'
import { typographyStyle } from '../../../theme/typographyStyle'
import { ProjectTagFilterSelect } from './ProjectTagFilterSelect'

const useStyles = makeStyles((theme) => ({
    iconButton: {
        padding: theme.spacing(1),
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    addButtonCell: {
        flex: '0 0 auto',
        width: '140px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    createProjectHeader: {
        ...(typographyStyle(theme.typography.h6) as Record<string, unknown>),
        fontWeight: 600,
        color: theme.palette.darkGreen.main,
        cursor: 'default',
        flex: '0 0 auto',
        width: '140px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(1),
        margin: theme.spacing(0, 0.5),
        textAlign: 'center',
    },
    headerCell: {
        ...(typographyStyle(theme.typography.h6) as Record<string, unknown>),
        fontWeight: 600,
        color: theme.palette.darkGreen.main,
        justifyContent: 'flex-start',
        flex: 1,
        padding: theme.spacing(1),
        margin: theme.spacing(0, 0.5),
    },
}))

interface Props {
    onClickRow: (analytic: ServiceOrderAnalytic) => void
    onAddProject: (analytic: ServiceOrderAnalytic) => void
    onManageBulkProjects?: () => void
    organizationId?: number
    selectedTagIds?: number[]
    onSelectedTagIdsChange?: (tagIds: number[]) => void
}

export const WorkspaceProjectsTable: React.FC<Props> = ({
    onClickRow,
    onAddProject,
    onManageBulkProjects,
    organizationId,
    selectedTagIds = [],
    onSelectedTagIdsChange,
}) => {
    const classes = useStyles()
    const theme = useTheme()

    // Sorting state
    const [orderBy, setOrderBy] = useState<string>(ApartmentNameASC)

    const {
        workspaceProjectAnalytics,
        workspaceProjectAnalyticsLoading,
        refetchWorkspaceProjectAnalytics,
        totalCount,
        numPages,
        start,
        end,
        pageSize,
        currentPage,
        goToPage,
        nextPage,
        prevPage,
        changePageSize,
    } = useWorkspaceProjectAnalytics({
        limit: 10,
        offset: 0,
        sort: orderBy,
        organizationId,
        tagIds: selectedTagIds,
    })

    const { userTags } = useUserTags()

    // Helper function to render tags
    const renderTags = (analytic: WorkspaceProjectsAnalytics) => {
        const relevantTags = userTags.filter((tag) =>
            tag.apartments.includes(analytic.id),
        )

        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexWrap: 'wrap',
                }}
            >
                {relevantTags.map((tag) => (
                    <Chip
                        key={tag.id}
                        label={tag.name}
                        size="small"
                        style={{
                            backgroundColor: tag.color,
                            color: 'white',
                            fontSize: '0.7rem',
                            height: '20px',
                        }}
                    />
                ))}
            </div>
        )
    }

    // Refetch when sort parameters change
    useEffect(() => {
        refetchWorkspaceProjectAnalytics()
    }, [orderBy, refetchWorkspaceProjectAnalytics])

    // Server-side sorting handler
    const handleSort = (
        field: string,
        sortAsc: string,
        sortDesc: string,
        getValue: (project: WorkspaceProjectsAnalytics) => any,
    ) => {
        const isAsc = orderBy === sortAsc
        const newOrderBy = isAsc ? sortDesc : sortAsc
        setOrderBy(newOrderBy)
    }

    return (
        <Container
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            <Container
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: theme.spacing(1),
                    marginRight: theme.spacing(1),
                    padding: theme.spacing(0, 1),
                    gap: theme.spacing(1),
                }}
            >
                {onSelectedTagIdsChange ? (
                    <ProjectTagFilterSelect
                        selectedTagIds={selectedTagIds}
                        onChange={onSelectedTagIdsChange}
                    />
                ) : (
                    <span />
                )}
                {onManageBulkProjects && (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={onManageBulkProjects}
                    >
                        Manage Turn Projects
                    </Button>
                )}
            </Container>
            {/* Table Container */}
            <Container
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100% - 100px)',
                }}
            >
                {/* Headers Container */}
                <Container
                    style={{
                        padding: theme.spacing(0, 1),
                        marginTop: theme.spacing(1),
                        flexShrink: 0,
                        minHeight: '48px',
                    }}
                >
                    <Container
                        className={classes.headerCell}
                        onClick={() => {
                            handleSort(
                                'apartment_name',
                                ApartmentNameASC,
                                ApartmentNameDESC,
                                (project: WorkspaceProjectsAnalytics) =>
                                    project.apartment_name,
                            )
                        }}
                    >
                        <TableSortLabel
                            active={
                                orderBy === ApartmentNameASC ||
                                orderBy === ApartmentNameDESC
                            }
                            direction={
                                orderBy === ApartmentNameASC ? 'asc' : 'desc'
                            }
                        >
                            Location
                        </TableSortLabel>
                    </Container>

                    <Container
                        className={classes.headerCell}
                        style={{
                            cursor: 'default',
                            alignItems: 'center',
                        }}
                    >
                        Tag
                    </Container>

                    <Container
                        className={classes.headerCell}
                        onClick={() => {
                            handleSort(
                                'total_created',
                                TotalCreatedASC,
                                TotalCreatedDESC,
                                (project: WorkspaceProjectsAnalytics) =>
                                    project.total_created,
                            )
                        }}
                    >
                        <TableSortLabel
                            active={
                                orderBy === TotalCreatedASC ||
                                orderBy === TotalCreatedDESC
                            }
                            direction={
                                orderBy === TotalCreatedASC ? 'asc' : 'desc'
                            }
                        >
                            Total Created
                        </TableSortLabel>
                    </Container>
                    <Container
                        className={classes.headerCell}
                        onClick={() => {
                            handleSort(
                                'active_count',
                                ActiveASC,
                                ActiveDESC,
                                (project: WorkspaceProjectsAnalytics) =>
                                    project.active_count,
                            )
                        }}
                    >
                        <TableSortLabel
                            active={
                                orderBy === ActiveASC || orderBy === ActiveDESC
                            }
                            direction={orderBy === ActiveASC ? 'asc' : 'desc'}
                        >
                            Active
                        </TableSortLabel>
                    </Container>
                    <Container
                        className={classes.headerCell}
                        onClick={() => {
                            handleSort(
                                'behind_count',
                                BehindASC,
                                BehindDESC,
                                (project: WorkspaceProjectsAnalytics) =>
                                    project.behind_count,
                            )
                        }}
                    >
                        <TableSortLabel
                            active={
                                orderBy === BehindASC || orderBy === BehindDESC
                            }
                            direction={orderBy === BehindASC ? 'asc' : 'desc'}
                        >
                            Behind
                        </TableSortLabel>
                    </Container>
                    <Container
                        className={classes.headerCell}
                        onClick={() => {
                            handleSort(
                                'future_count',
                                FutureASC,
                                FutureDESC,
                                (project: WorkspaceProjectsAnalytics) =>
                                    project.future_count,
                            )
                        }}
                    >
                        <TableSortLabel
                            active={
                                orderBy === FutureASC || orderBy === FutureDESC
                            }
                            direction={orderBy === FutureASC ? 'asc' : 'desc'}
                        >
                            Future
                        </TableSortLabel>
                    </Container>
                    <Container
                        className={classes.headerCell}
                        onClick={() => {
                            handleSort(
                                'total_active_schedules',
                                ActiveProjectsASC,
                                ActiveProjectsDESC,
                                (project: WorkspaceProjectsAnalytics) =>
                                    project.total_active_schedules,
                            )
                        }}
                    >
                        <TableSortLabel
                            active={
                                orderBy === ActiveProjectsASC ||
                                orderBy === ActiveProjectsDESC
                            }
                            direction={
                                orderBy === ActiveProjectsASC ? 'asc' : 'desc'
                            }
                        >
                            Active Projects
                        </TableSortLabel>
                    </Container>
                    <Container
                        className={classes.headerCell}
                        onClick={() => {
                            handleSort(
                                'future_schedules',
                                FutureProjectsASC,
                                FutureProjectsDESC,
                                (project: WorkspaceProjectsAnalytics) =>
                                    project.future_schedules,
                            )
                        }}
                    >
                        <TableSortLabel
                            active={
                                orderBy === FutureProjectsASC ||
                                orderBy === FutureProjectsDESC
                            }
                            direction={
                                orderBy === FutureProjectsASC ? 'asc' : 'desc'
                            }
                        >
                            Future Projects
                        </TableSortLabel>
                    </Container>
                    <Container
                        className={classes.headerCell}
                        onClick={() => {
                            handleSort(
                                'most_recent_date',
                                RecentASC,
                                RecentDESC,
                                (project: WorkspaceProjectsAnalytics) =>
                                    project.most_recent_date,
                            )
                        }}
                    >
                        <TableSortLabel
                            active={
                                orderBy === RecentASC || orderBy === RecentDESC
                            }
                            direction={orderBy === RecentASC ? 'asc' : 'desc'}
                        >
                            Most Recent Created
                        </TableSortLabel>
                    </Container>
                    <Container className={classes.createProjectHeader}>
                        Create New Project
                    </Container>
                </Container>

                {/* Rows Container */}
                <Container
                    style={{
                        overflowY: 'auto',
                        flex: 1,
                        minHeight: '200px',
                        maxHeight: 'calc(100vh - 280px)',
                    }}
                    direction="column"
                >
                    {workspaceProjectAnalyticsLoading ? (
                        <Container
                            style={{
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1,
                            }}
                        >
                            <CircularProgress size={100} />
                        </Container>
                    ) : (
                        workspaceProjectAnalytics?.data.results.map(
                            (
                                serviceOrderAnalytic: WorkspaceProjectsAnalytics,
                            ) => {
                                const columns: (
                                    | string
                                    | number
                                    | React.ReactNode
                                )[] = [
                                    serviceOrderAnalytic.apartment_name,
                                    renderTags(serviceOrderAnalytic),
                                    serviceOrderAnalytic.total_created,
                                    serviceOrderAnalytic.active_count,
                                    serviceOrderAnalytic.behind_count,
                                    serviceOrderAnalytic.future_count,
                                    serviceOrderAnalytic.total_active_schedules,
                                    serviceOrderAnalytic.future_schedules,
                                    serviceOrderAnalytic.most_recent_date
                                        ? new Date(
                                              serviceOrderAnalytic.most_recent_date as string,
                                          ).toLocaleDateString()
                                        : 'N/A',
                                    <Container
                                        key={`add-project-${serviceOrderAnalytic.id}`}
                                        className={classes.addButtonCell}
                                    >
                                        <Tooltip title="Create New Project">
                                            <IconButton
                                                color="primary"
                                                className={classes.iconButton}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    onAddProject(
                                                        serviceOrderAnalytic,
                                                    )
                                                }}
                                            >
                                                <AddIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Container>,
                                ]

                                return (
                                    <WorkspaceProjectsRow
                                        key={`SERVICE_ORDER_APARTMENT_${serviceOrderAnalytic.id}`}
                                        theme={theme}
                                        columns={columns}
                                        onClickRow={() =>
                                            onClickRow(serviceOrderAnalytic)
                                        }
                                        nonFlexCells={[9]}
                                        nonFlexCellWidths={{ 9: '140px' }}
                                    />
                                )
                            },
                        )
                    )}
                </Container>
            </Container>

            {/* Pagination Container */}
            <Container>
                <Container
                    style={{
                        ...typographyStyle(theme.typography.subtitle1),
                        borderTop: `1px solid ${theme.palette.grey[400]}`,
                        padding: theme.spacing(1),
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexShrink: 0,
                        minHeight: '56px',
                        backgroundColor: theme.palette.background.paper,
                        position: 'sticky',
                        bottom: 0,
                    }}
                >
                    <Container
                        style={{ alignItems: 'center', gap: theme.spacing(1) }}
                    >
                        <span>Rows per page:</span>
                        <FormControl size="small" style={{ minWidth: 80 }}>
                            <Select
                                value={pageSize}
                                onChange={(e) =>
                                    changePageSize(e.target.value as number)
                                }
                                displayEmpty
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                            </Select>
                        </FormControl>
                    </Container>

                    <Container
                        style={{ alignItems: 'center', gap: theme.spacing(2) }}
                    >
                        <span>
                            {start} - {end} of {totalCount}
                        </span>
                        <Pagination
                            count={numPages}
                            page={currentPage}
                            onChange={(_: unknown, newPage: number) => {
                                goToPage(newPage)
                            }}
                            size="small"
                        />
                    </Container>
                </Container>
            </Container>
        </Container>
    )
}
